import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[#4361C2] outline-none py-4 text-base font-light placeholder:text-ash/60 transition-colors duration-300 rounded-none";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", project_type: "Custom Home", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We will be in touch within one business day.");
      setForm({ name: "", email: "", project_type: "Custom Home", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="px-6 md:px-12 py-28 md:py-40" data-testid="contact-section">
      <SectionHeading index="Start Here" title="Let's Build" testId="contact-heading" />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24">
        <FadeUp className="md:col-span-5">
          <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-bold tracking-tight leading-[0.95]">
            Every structure starts with a conversation.
          </h3>
          <div className="mt-14 space-y-8 text-sm">
            <div data-testid="contact-email-row">
              <div className="text-xs tracking-[0.3em] uppercase text-ash mb-2">Email</div>
              <div className="flex flex-col gap-1">
                <a href="mailto:LonnyLBradley@gmail.com" className="link-line text-lg font-light w-fit" data-testid="contact-email-link">
                  LonnyLBradley@gmail.com
                </a>
                <a href="mailto:Lbradley@etmbdesignbuild.com" className="link-line text-lg font-light w-fit" data-testid="contact-email-link-2">
                  Lbradley@etmbdesignbuild.com
                </a>
              </div>
            </div>
            <div data-testid="contact-phone-row">
              <div className="text-xs tracking-[0.3em] uppercase text-ash mb-2">Phone</div>
              <a href="tel:+12029975954" className="link-line text-lg font-light" data-testid="contact-phone-link">
                202.997.5954
              </a>
            </div>
            <div data-testid="contact-office-row">
              <div className="text-xs tracking-[0.3em] uppercase text-ash mb-2">Studio</div>
              <p className="text-lg font-light">Burke, Virginia</p>
            </div>
            <div data-testid="contact-licensing-row">
              <div className="text-xs tracking-[0.3em] uppercase text-ash mb-2">Licensed &amp; Insured</div>
              <ul className="text-lg font-light space-y-1">
                <li>Washington DC</li>
                <li>Virginia</li>
                <li>Florida</li>
                <li>Maryland <span className="text-ash text-sm">(pending)</span></li>
                <li>New York <span className="text-ash text-sm">(pending)</span></li>
              </ul>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="md:col-span-7">
          <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="contact-name" className="text-xs tracking-[0.3em] uppercase text-ash">Name</label>
                <input
                  id="contact-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs tracking-[0.3em] uppercase text-ash">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-type" className="text-xs tracking-[0.3em] uppercase text-ash">Project Type</label>
              <select
                id="contact-type"
                value={form.project_type}
                onChange={set("project_type")}
                className={`${inputCls} appearance-none cursor-pointer`}
                data-testid="contact-type-select"
              >
                {["Custom Home", "Commercial", "Renovation / Addition", "Design Only", "Other"].map((o) => (
                  <option key={o} value={o} className="bg-[#0A0A0A] text-paper">{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="text-xs tracking-[0.3em] uppercase text-ash">The Project</label>
              <textarea
                id="contact-message"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Site, scope, timeline — tell us what you're imagining."
                className={`${inputCls} resize-none`}
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-3 border border-white px-10 py-5 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-black disabled:opacity-50 transition-colors duration-300"
              data-testid="contact-submit-button"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
